import Head from "next/head";
import cn from "classnames";
import TitleProduct from "components/sections/TitleProduct";
import Accordion from "components/sections/Accordion";
import Button from "components/UI/Button";
import useScrollTop from "../../hooks/useScrollTop";
import Link from "next/link";
import { track } from "../../utils/amplitude";
import { PromoBanner } from "components/Home/Promo";
import useWindowWidth from "hooks/useWindowWidth";
import ReviewContainer from "components/UI/ReviewContainer";
import Stats from "components/sections/Stats";
import DescriptionNew from "components/sections/DescriptionNew";

const data = [
  {
    text: "99,99% uptime",
    src: "/stats/startup.webp",
  },
  {
    text: "99,9% data accuracy",
    src: "/stats/measure.webp",
  },
  {
    text: "20+ integrations",
    src: "/stats/infrastructure.webp",
  },
  {
    text: "Open source SDKs",
    src: "/stats/build.webp",
  },
  {
    text: "4 supported platforms",
    src: "/stats/robot.webp",
  },
  {
    text: "GDPR/CCPA Compliance",
    src: "/stats/check.webp",
  },
];

export default function development() {
  const width = useWindowWidth();
  useScrollTop();

  return (
    <div className="wrapper">
      <Head>
        <title>
          Easily integrate in-app subscriptions with Apphud – Apphud
        </title>
        <link rel="canonical" href="https://apphud.com/development" />
        <meta
          property="og:title"
          content="Mobile app service for integration with iOS and Android in app subscriptions – Apphud"
        />
        <meta
          property="og:description"
          content="Integrate in app subscriptions in 3 lines of code. Mobile app service for integrations with iOS and Android in app subscriptions: implement offers without server."
        />
        <meta property="og:url" content="https://apphud.com/development" />
        <meta
          property="og:image"
          content="/images/og/im_6.webp"
          key="og:image"
        />
        <meta
          property="twitter:image"
          content="/images/og/im_6.webp"
          key="twitter:image"
        />
        <meta
          property="twitter:title"
          content="Mobile app service for integration with iOS and Android in app subscriptions – Apphud"
        />
        <meta
          property="twitter:description"
          content="Integrate in app subscriptions in 3 lines of code. Mobile app service for integrations with iOS and Android in app subscriptions: implement offers without server."
        />
        <meta property="twitter:url" content="https://apphud.com/development" />
        <meta
          name="description"
          content="Add in-app purchases to your app in minutes. Get subscription stats through powerful SDKs. Validate in-app purchases and implement subscription offers with no code."
        />
      </Head>

      <TitleProduct
        title={"Easily integrate in-app\n subscriptions "}
        titleStyles={width > 922 ? {} : { fontSize: 38 }}
        text={
          "Apphud is a reliable subscription infrastructure with powerful SDKs for iOS, Android, Flutter, and React Native apps"
        }
        textStyles={width > 922 ? {} : { paddingTop: 7 }}
        limitWidth
        actions={
          <>
            <Link
              href="/demo"
              target="_blank"
              onClick={() =>
                track("demo_request_link_clicked", { block: "top_link" })
              }
            >
              <Button title="Book a demo" />
            </Link>
            <a
              style={{ marginLeft: 40 }}
              target="_blank"
              rel="noopener noreferrer"
              href="https://app.apphud.com/sign_up"
              onClick={() =>
                track("sign_up_button_clicked", { block: "top_link" })
              }
            >
              <Button title="Start for free" transparent />
            </a>
          </>
        }
        actionStyles={width > 922 ? {} : { paddingTop: 24 }}
      />

      <Stats data={data} />

      <DescriptionNew
        title="Integrate in-app subscriptions in 3 lines of code"
        description="Add any type of in-app purchase without writing a single line ofcustom code or setting up a server. Check subscription stateacross different platforms"
        descriptionStyles={
          width > 922 ? {} : { marginTop: "-35px", textAlign: "center" }
        }
        img="making-in-app2"
        alt="mobile-in-app"
      />

      <DescriptionNew
        reverse
        title="Automatically validate in-app subscriptions"
        description="Securely validate App Store and Google Play receipts without server code. Prevent fraudulent transactions and do not unlock premium features for fraudsters"
        descriptionStyles={width > 922 ? {} : { textAlign: "center" }}
        img="validate"
        alt="validate"
        additionalInfo
        aiIcon="tools"
        aiContent="Try Apphud without SDK implementation in Observer mode. Feel the power of accurate data"
      />

      <DescriptionNew
        title={"Send subscription events to your server or third-parties"}
        description="Enrich your in-house analytics by sending subscription eventsdirectly to your solution. 
        Export raw data or use 20+ integrations to make informeddecisions"
        descriptionStyles={width > 922 ? {} : { textAlign: "center" }}
        linkPath="https://docs.apphud.com/docs"
        linkText="Explore documentation"
        img="send-subscriptions"
        alt="send-subscriptions"
        onClick={() =>
          track("page_external_link_clicked", {
            target_url: "https://docs.apphud.com/docs",
          })
        }
      />

      <DescriptionNew
        reverse
        title="Remotely manage paywalls"
        description="Configure your payment screens and run A/B experiments to prove product hypotheses and increase conversions and app revenue"
        descriptionStyles={width > 922 ? {} : { textAlign: "center" }}
        img="run-experiments-alt"
        alt="run-experiments"
      />

      <DescriptionNew
        title="Effortlessly convert your users with promotional offers"
        description="Provide a discount to lapsed customers by using Apphud promotional offers. Reduce churn rate automatically without server code"
        descriptionStyles={width > 922 ? {} : { textAlign: "center" }}
        img="convert-users"
        alt="convert-users"
      />

      <div
        style={
          width > 922
            ? { display: "flex", justifyContent: "center" }
            : { padding: "0 16px" }
        }
      >
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://app.apphud.com/sign_up"
          onClick={() => track("sign_up_button_clicked", { block: "top_link" })}
        >
          <Button title="Start for free" />
        </a>
      </div>

      <div
        className={width > 922 ? "container" : ""}
        style={{ margin: width > 922 ? "160px auto" : "60px auto" }}
      >
        <ReviewContainer
          description={
            <div>
              Check the case study to see how PlantMe{" "}
              <a style={{ color: "var(--0084FF, #0084FF)" }}>
                adjusted its app revenue 5 times in just half a year
              </a>{" "}
              by delegating subscription tracking and analytics to Apphud
            </div>
          }
          title={"Plantme"}
          src="/icons/img104.webp"
        />
      </div>

      <div
        className={cn(width > 922 ? "container" : "")}
        style={{ marginBottom: width > 922 ? "" : 20 }}
      >
        <PromoBanner text={"Getting started is easy. Try Apphud today"} />
      </div>

      <div
        className="accordion"
        style={width > 922 ? { marginTop: 80 } : { marginBottom: "-57px" }}
      >
        <div className="container">
          <h2>Got a question?</h2>
          <div className="accordion-items">
            <Accordion title="What SDKs do you have?">
              We have iOS SDK written in Swift, Android SDK written in Kotlin,
              Flutter SDK, and ReactNative SDK. Our SDKs are open-source and
              published{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://github.com/apphud"
                onClick={() =>
                  track("page_external_link_clicked", {
                    target_url: "https://github.com/apphud",
                  })
                }
              >
                here
              </a>
              .
            </Accordion>
            <Accordion title="Do I need to rewrite my purchase flow using Apphud SDK?">
              No. You don't need to replace your existing code with the Apphud
              SDK. You can run Apphud in observer mode and just listen for
              purchases
            </Accordion>
            <Accordion title="Do you have a REST API?">
              Yes. We have REST API to get current customers by their user ID.
              REST API is available on paid plans
            </Accordion>
            <Accordion title="What are Webhooks? Do you have any?">
              Apphud can send POST requests to your server about subscription
              events. These requests are called Webhooks (server-to-server
              notifications). They are available on paid plans. You can use
              these events to implement custom logic or send data to in-house
              analytics. Read more about webhooks{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.apphud.com/docs/webhook"
                onClick={() =>
                  track("page_external_link_clicked", {
                    target_url: "https://docs.apphud.com/docs/webhook",
                  })
                }
              >
                here
              </a>
              .
            </Accordion>
            <Accordion title="What will happen if your server is down? Is Apphud stable?">
              Apphud has 99,99% uptime. You can check it out{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://status.apphud.com/"
                onClick={() =>
                  track("page_external_link_clicked", {
                    target_url: "https://status.apphud.com/",
                  })
                }
              >
                here
              </a>
              . Most of the data is cached on the device. However, if there are
              issues with subscription verification, our SDK tries to re-submit
              data until success
            </Accordion>
            <Accordion title="Can I use Apphud to sign my subscription offer?">
              Yes, you can. Just follow{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.apphud.com/docs/promo-offers"
                onClick={() =>
                  track("page_external_link_clicked", {
                    target_url: "https://docs.apphud.com/docs/promo-offers",
                  })
                }
              >
                this guide
              </a>
              .
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
}
